import { EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { capitalize } from 'lodash';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import CalendarHeader from '../../components/CalendarHeader';
import { useLanguage } from '../../context/language';
import { CourseInstanceBoxComp } from '../../courseInstance/components/CourseInstanceBox';
import { getFullCalendarLocale } from '../../utils/language';
import { OpenClassCalendarDay } from '../utils/openClassCalendar';

type OpenClassCalendarProps = {
  fetchedEvents: OpenClassCalendarDay[];
  loadingOpenClasses?: boolean;
  onClickDay: (date: Date | null) => void;
};

const opacityArray = ['0.1', '0.25', '0.35', '0.5', '0.7', '0.8', '1'];

const OpenClassCalendar: React.FC<OpenClassCalendarProps> = ({
  fetchedEvents,
  loadingOpenClasses,
  onClickDay,
}) => {
  const calendarRef = useRef<FullCalendar>(null);
  const [currentLanguage] = useLanguage();
  const { t } = useTranslation();

  const events = useMemo<EventInput[]>(
    () =>
      fetchedEvents.map((event, index) => ({
        id: String(index),
        title: String(event.count),
        date: event.date,
        textColor: '#000000',
        backgroundColor: '#FFFFFF00',
        borderColor: '#FFFFFF00',
      })),
    [fetchedEvents],
  );

  return (
    <>
      <CalendarHeader calendar={calendarRef} loading={loadingOpenClasses} />
      <FullCalendar
        weekNumbers
        ref={calendarRef}
        dayCellClassNames="hover:bg-calendar"
        headerToolbar={false}
        events={events}
        selectMirror={true}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        eventClick={(info) => {
          onClickDay?.(info.event.start);
        }}
        dateClick={(info) => {
          onClickDay?.(info.date);
        }}
        contentHeight={900}
        dayHeaderContent={function (arg) {
          return capitalize(arg.text);
        }}
        firstDay={1}
        locale={getFullCalendarLocale(currentLanguage?.code)}
        eventClassNames={
          'hover:bg-calendar m-0 absolute w-full h-80 flex justify-self-center overflow-hidden'
        }
        eventContent={function (arg) {
          return (
            <CourseInstanceBoxComp
              countLabel={(count) =>
                t('views.OpenClasses.openClassCount', { count })
              }
              opacity={opacityArray[+arg.event.title]}
              event={arg.event}
            />
          );
        }}
      />
    </>
  );
};

export default OpenClassCalendar;
